import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Lock } from 'lucide-react';
import { OPEN_CONSENT_SETTINGS_EVENT, readConsent, saveConsent } from '@/lib/consent';

const CHOICE_BUTTON =
  'flex-1 min-h-[44px] px-4 py-2.5 rounded-lg text-sm font-semibold border-2 border-bmg-ink transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-bmg-yellow focus-visible:ring-offset-2';

export default function CookieBanner() {
  const [open, setOpen] = useState(() => readConsent() === null);
  const [showSettings, setShowSettings] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const marketingId = useId();
  const necessaryId = useId();

  const openSettings = useCallback(() => {
    returnFocusRef.current = document.activeElement as HTMLElement | null;
    setMarketing(readConsent()?.marketing ?? false);
    setShowSettings(true);
    setOpen(true);
  }, []);

  useEffect(() => {
    window.addEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(OPEN_CONSENT_SETTINGS_EVENT, openSettings);
  }, [openSettings]);

  useEffect(() => {
    if (open) dialogRef.current?.focus();
  }, [open, showSettings]);

  const choose = (allowMarketing: boolean) => {
    saveConsent(allowMarketing);
    setOpen(false);
    setShowSettings(false);
    returnFocusRef.current?.focus?.();
    returnFocusRef.current = null;
  };

  if (!open) return null;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-label="Samtykke til informasjonskapsler"
      aria-modal="false"
      tabIndex={-1}
      className="fixed z-[60] bottom-3 left-3 right-3 sm:left-auto sm:right-5 sm:bottom-5 sm:w-[420px] max-h-[calc(100vh-24px)] overflow-y-auto rounded-2xl bg-white text-bmg-ink border border-bmg-gray-border shadow-[0_12px_40px_rgba(0,0,0,0.18)] p-4 sm:p-5 focus:outline-none animate-[slideUp_0.25s_ease-out]"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0 w-9 h-9 rounded-full bg-bmg-yellow-light flex items-center justify-center" aria-hidden="true">
          <Cookie className="w-5 h-5 text-bmg-yellow-text" />
        </span>
        <p className="text-sm leading-relaxed text-bmg-ink-soft">
          Vi bruker informasjonskapsler for at nettsiden skal fungere. Hvis du sier ja, bruker vi dem også til å måle
          hvilke annonser som gir bestillinger. Du kan når som helst endre valget ditt.{' '}
          <Link
            to="/personvern#informasjonskapsler"
            className="font-semibold text-bmg-ink underline underline-offset-2 hover:text-bmg-yellow-text"
          >
            Les mer
          </Link>
        </p>
      </div>

      {showSettings && (
        <fieldset className="mt-4 space-y-2">
          <legend className="sr-only">Velg informasjonskapsler</legend>
          <div className="flex items-center justify-between gap-3 rounded-lg bg-bmg-gray px-3 py-2.5">
            <label htmlFor={necessaryId} className="text-sm font-semibold flex items-center gap-2">
              <Lock className="w-3.5 h-3.5 text-bmg-ink-soft" aria-hidden="true" />
              Nødvendige
              <span className="font-normal text-xs text-bmg-ink-soft">Alltid på</span>
            </label>
            <input id={necessaryId} type="checkbox" checked disabled className="w-5 h-5 accent-bmg-ink" />
          </div>
          <div className="flex items-center justify-between gap-3 rounded-lg bg-bmg-gray px-3 py-2.5">
            <label htmlFor={marketingId} className="text-sm font-semibold cursor-pointer">
              Markedsføring (Google Ads)
            </label>
            <input
              id={marketingId}
              type="checkbox"
              checked={marketing}
              onChange={(e) => setMarketing(e.target.checked)}
              className="w-5 h-5 accent-bmg-ink cursor-pointer"
            />
          </div>
          <button
            type="button"
            onClick={() => choose(marketing)}
            className={`${CHOICE_BUTTON} w-full mt-2 bg-bmg-yellow hover:bg-bmg-yellow-dark`}
          >
            Lagre valg
          </button>
        </fieldset>
      )}

      <div className="mt-4 flex gap-2">
        <button type="button" onClick={() => choose(true)} className={`${CHOICE_BUTTON} bg-bmg-ink text-white hover:bg-bmg-ink-soft`}>
          Godta alle
        </button>
        <button type="button" onClick={() => choose(false)} className={`${CHOICE_BUTTON} bg-bmg-ink text-white hover:bg-bmg-ink-soft`}>
          Kun nødvendige
        </button>
      </div>
      {!showSettings && (
        <button
          type="button"
          onClick={() => setShowSettings(true)}
          aria-expanded={showSettings}
          className="mt-2 w-full min-h-[40px] text-sm font-semibold text-bmg-ink underline underline-offset-2 rounded-lg hover:text-bmg-yellow-text focus:outline-none focus-visible:ring-2 focus-visible:ring-bmg-yellow"
        >
          Innstillinger
        </button>
      )}
    </div>
  );
}
